/**
 * T563 (FR-UI-005) — Animation-Discipline hook + context provider.
 *
 * Returns whether decorative animation should run for the surrounding
 * view. Resolution order (any layer can force OFF; nothing can force
 * ON over a stronger OFF):
 *
 *   1. `prefers-reduced-motion: reduce` (OS-level accessibility)
 *   2. Context-aware off-list (`detail-edit`, `durcharbeite`, `modal`)
 *   3. Master user-setting (`animation_enabled` in localStorage,
 *      default true) — same key the existing /settings toggle and
 *      `__root.tsx` master-toggle already consume.
 *
 * The hook reads context via React.Context; views opt in by wrapping
 * the relevant subtree in `<AnimationContextProvider context="...">`.
 * Unwrapped views default to `'idle'` (animation on, unless 1 or 3
 * forces off).
 */
import { createContext, createElement, type ReactNode, useContext } from 'react';

export type AnimationContext = 'idle' | 'detail-edit' | 'durcharbeite' | 'modal';

// Off-list: contexts where the user is performing precise input or
// reading dense data — chrome animation is friction here.
const ANIMATION_OFF_CONTEXTS: ReadonlySet<AnimationContext> = new Set([
  'detail-edit',
  'durcharbeite',
  'modal',
]);

const Ctx = createContext<AnimationContext>('idle');

interface ProviderProps {
  context: AnimationContext;
  children: ReactNode;
}

export function AnimationContextProvider({ context, children }: ProviderProps) {
  return createElement(Ctx.Provider, { value: context }, children);
}

export interface UseAnimationEnabledInput {
  /** When provided, overrides the React-context lookup — useful in
   * tests and in views that want to compute a hypothetical without
   * mounting under a provider. */
  contextOverride?: AnimationContext;
  /** When provided, overrides the localStorage read (test seam). */
  masterOverride?: boolean;
  /** When provided, overrides the OS reduced-motion read (test seam). */
  reducedMotionOverride?: boolean;
}

export function resolveAnimationEnabled(input: {
  reducedMotion: boolean;
  context: AnimationContext;
  master: boolean;
}): boolean {
  if (input.reducedMotion) return false;
  if (ANIMATION_OFF_CONTEXTS.has(input.context)) return false;
  return input.master;
}

function readReducedMotion(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  } catch {
    return false;
  }
}

function readMaster(): boolean {
  if (typeof window === 'undefined') return true;
  try {
    const raw = window.localStorage.getItem('animation_enabled');
    if (raw === '0' || raw === 'false') return false;
    return true;
  } catch {
    return true;
  }
}

export function useAnimationEnabled(input: UseAnimationEnabledInput = {}): boolean {
  const ctxFromTree = useContext(Ctx);
  return resolveAnimationEnabled({
    reducedMotion: input.reducedMotionOverride ?? readReducedMotion(),
    context: input.contextOverride ?? ctxFromTree,
    master: input.masterOverride ?? readMaster(),
  });
}

// Re-exported so test files can hit the pure resolver without a hook.
export const __testing = {
  ANIMATION_OFF_CONTEXTS,
};
